import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import Table from "../../Components/Table";
import AddTaskModal from "../../Components/AddTaskModal";
import TaskFilterModal from "../../Components/TaskFilterModal";
import { useAuth } from "../../Context/authContext";
import { getTasksByUserId, getIdeasByUserId, createUserTask } from "../../Utils/apiServices";

// Format date from API (YYYY-MM-DD) to display format (e.g., "11th Dec")
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const daySuffix = getDaySuffix(day);
    return `${day}${daySuffix} ${month}`;
  } catch (error) {
    return dateString;
  }
};

const getDaySuffix = (day) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const Component = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [allTasks, setAllTasks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [tasksResponse, ideasResponse] = await Promise.all([
        getTasksByUserId(user.id),
        getIdeasByUserId(user.id),
      ]);

      let tasksData = [];
      if (tasksResponse.success) {
        if (Array.isArray(tasksResponse.data?.data)) {
          tasksData = tasksResponse.data.data;
        } else if (Array.isArray(tasksResponse.data)) {
          tasksData = tasksResponse.data;
        } else if (tasksResponse.data?.data?.data && Array.isArray(tasksResponse.data.data.data)) {
          tasksData = tasksResponse.data.data.data;
        }
      }

      let ideasData = [];
      if (ideasResponse.success) {
        const raw = ideasResponse.data?.data ?? ideasResponse.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        ideasData = list.map((item) => {
          const ideaObj = item.idea || item;
          const id = ideaObj.id ?? item.idea_id;
          const title = ideaObj.title ?? ideaObj.name ?? "";
          return { id, title, ...ideaObj };
        });
      }

      const ideasMap = {};
      ideasData.forEach((idea) => {
        const ideaName = idea.name || idea.title || idea.idea_name || idea.startupName || "";
        if (idea.id != null) {
          ideasMap[idea.id] = ideaName;
        }
      });

      const mappedTasks = tasksData.map((task) => {
        const ideaId = task.idea_id || task.ideaId;
        const ideaName = ideaId ? ideasMap[ideaId] || "" : "";
        return {
          id: task.id,
          task: task.task_details || task.task || "",
          idea: ideaName,
          student: task.student_name || task.student || "",
          status: task.status || "Pending",
          priority: task.priority || "Medium",
          dueDate: formatDate(task.end_date || task.due_date),
          completed: task.is_completed || task.completed || false,
          _original: task,
        };
      });

      setAllTasks(mappedTasks);
      setIdeas(ideasData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-500 text-white";
      case "In Progress":
        return "bg-purple-500 text-white";
      case "Pending":
        return "bg-orange-500 text-white";
      default:
        return "bg-slate-200 text-slate-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "text-red-500";
      case "Medium":
        return "text-orange-500";
      case "Low":
        return "text-green-500";
      default:
        return "text-slate-500";
    }
  };

  const handleAddTask = async (payload) => {
    try {
      const payloadWithClient = { ...payload, client_id: user?.id };
      const response = await createUserTask(payloadWithClient);
      if (response.success) {
        await fetchData();
        return { success: true };
      }
      return { success: false, error: response.error || "Failed to create task." };
    } catch (err) {
      return { success: false, error: err?.error || err?.message || "An error occurred while creating the task." };
    }
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
  };

  // Filter tasks based on active filters and search term
  const filteredTasks = useMemo(() => {
    return allTasks.filter((task) => {
      if (activeFilters.status && task.status !== activeFilters.status) {
        return false;
      }
      if (activeFilters.priority && task.priority !== activeFilters.priority) {
        return false;
      }
      if (activeFilters.idea && task.idea !== activeFilters.idea) {
        return false;
      }
      if (activeFilters.student && task.student !== activeFilters.student) {
        return false;
      }
      // Search filter
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const studentName = (task.student || "").toLowerCase();
        const taskName = (task.task || "").toLowerCase();
        const ideaName = (task.idea || "").toLowerCase();
        if (!studentName.includes(searchLower) && !taskName.includes(searchLower) && !ideaName.includes(searchLower)) {
          return false;
        }
      }
      return true;
    });
  }, [allTasks, activeFilters, searchTerm]);

  const hasActiveFilters = () => {
    return Object.values(activeFilters).some((value) => value !== "");
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters).filter((value) => value !== "").length;
  };

  const columns = [
    {
      key: "task",
      header: "Task",
      accessor: "task",
    },
    {
      key: "idea",
      header: "Idea",
      accessor: "idea",
    },
    // {
    //   key: "student",
    //   header: "Student",
    //   accessor: "student",
    // },
    {
      key: "status",
      header: "Status",
      accessor: "status",
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
            value
          )}`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      accessor: "priority",
      render: (value) => (
        <div className="flex items-center gap-1.5">
          <Icon
            icon="lucide:dot"
            className={`h-4 w-4 ${getPriorityColor(value)}`}
          />
          <span className="text-sm text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: "dueDate",
      header: "Due Date",
      accessor: "dueDate",
      sortable: false,
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
        <Typography as="p" className="text-md text-tertiary-600">
          Loading tasks...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
      {/* Header Section */}
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col gap-2 items-start justify-start">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Tasks
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            Manage tasks assigned to students and track completion status.
          </Typography>
        </div>
        <Button
          variant="secondary"
          size="medium"
          startIcon={<Icon icon="lucide:plus" className="h-5 w-5" />}
          onClick={() => setIsModalOpen(true)}
        >
          Add New Tasks
        </Button>
      </div>

      {/* All Tasks Card */}
      <Card
        className="w-full shadow-sm"
        header={
          <div className="flex items-center justify-between w-full">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              All Tasks
            </Typography>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
                <Icon
                  icon="lucide:search"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
                />
              </div>
              {hasActiveFilters() && (
                <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium">
                  <Icon icon="lucide:filter" className="h-3 w-3" />
                  {getActiveFilterCount()} active
                </div>
              )}
              <Button
                variant="secondary"
                size="small"
                startIcon={<Icon icon="lucide:filter" className="h-4 w-4" />}
                onClick={() => setIsFilterModalOpen(true)}
              >
                Filters
              </Button>
            </div>
          </div>
        }
        bodyClassName="p-0 overflow-x-auto"
      >
        <Table
          columns={columns}
          data={filteredTasks}
          rowKey="id"
          emptyMessage="No tasks available"
        />
      </Card>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddTask}
        ideas={ideas}
      />

      {/* Filter Modal */}
      <TaskFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleApplyFilters}
        filters={activeFilters}
        ideas={ideas}
      />
    </div>
  );
};

export default Component;
